import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from 'src/lib/api/axios';

// ---------------------------------------------------------------------------
// Analysis / extracted fields
// ---------------------------------------------------------------------------

export type ExtractedFieldStatus = 'suggested' | 'approved' | 'rejected';
export type ExtractedFieldSource = 'llm' | 'human';
export type FieldValueType = 'boolean' | 'string' | 'date' | 'decimal' | 'enum';

export interface ExtractedField {
  id: string;
  document_id: string;
  key: string;
  raw_value: string;
  normalized_value: string | null;
  value_type: FieldValueType;
  confidence: number | null;
  source: ExtractedFieldSource;
  status: ExtractedFieldStatus;
  chunk_ids: string[];
}

export async function getDocumentFields(documentId: string): Promise<ExtractedField[]> {
  const response = await apiGet<ExtractedField[]>(`files/analysis/${documentId}/fields`, {
    meta: { globalError: false, context: 'get-document-fields' }
  });
  return response.data;
}

/** The issue and expiry dates the hub holds for a document, by the rule Evidence Documents are judged on. */
export interface DocumentDates {
  /** Null where the hub holds no issue date. */
  issued_on: string | null;
  /** Null where the hub holds no expiry date. */
  expires_on: string | null;
  /** Whether the requester may change these dates. */
  can_edit: boolean;
}

export async function getDocumentDates(documentId: string): Promise<DocumentDates> {
  const response = await apiGet<DocumentDates>(`files/analysis/${documentId}/dates`, {
    meta: { globalError: false, context: 'get-document-dates' }
  });
  return response.data;
}

export interface FieldHistoryEntry {
  action: 'ADD' | 'MODIFY' | 'DELETE';
  action_time: string;
  source: 'llm' | 'human';
  status: 'suggested' | 'approved' | 'rejected';
  raw_value: string;
  user_name: string | null;
}

export async function getFieldHistory(fieldId: string): Promise<FieldHistoryEntry[]> {
  const response = await apiGet<FieldHistoryEntry[]>(`files/analysis/fields/${encodeURIComponent(fieldId)}/history`, {
    meta: { globalError: false, context: 'field-history' }
  });
  return response.data;
}

export async function getDocumentTagHistory(
  documentId: string,
  groups: Array<'document_type' | 'domain' | 'site' | 'national_returns'>
): Promise<FieldHistoryEntry[]> {
  const qs = new URLSearchParams();
  groups.forEach((g) => qs.append('group', g));
  const response = await apiGet<FieldHistoryEntry[]>(`resources/${documentId}/tags/history?${qs.toString()}`, {
    meta: { globalError: false, context: 'document-tag-history' }
  });
  return response.data;
}

export async function deleteField(fieldId: string): Promise<void> {
  await apiDelete(`files/analysis/fields/${fieldId}`, {
    meta: { globalError: false, context: 'delete-field' }
  });
}

export async function reviewField(fieldId: string, status: 'approved' | 'rejected'): Promise<ExtractedField> {
  const response = await apiPost<ExtractedField>(
    `files/analysis/fields/${fieldId}/review`,
    { status },
    { meta: { globalError: false, context: 'review-field' } }
  );
  return response.data;
}

export async function updateField(fieldId: string, rawValue: string): Promise<ExtractedField> {
  const response = await apiPatch<ExtractedField>(
    `files/analysis/fields/${fieldId}`,
    { raw_value: rawValue },
    { meta: { globalError: false, context: 'update-field' } }
  );
  return response.data;
}

export async function addDocumentField(documentId: string, key: string, rawValue: string): Promise<ExtractedField> {
  const response = await apiPost<ExtractedField>(
    `files/analysis/${documentId}/fields`,
    { key, raw_value: rawValue },
    { meta: { globalError: false, context: 'add-document-field' } }
  );
  return response.data;
}

export interface ResourceTagAssignment {
  id: string;
  tag_id: string;
  group: 'document_type' | 'domain' | 'site' | 'national_returns';
  slug: string;
  origin: 'llm' | 'human' | 'inherited';
  status: ExtractedFieldStatus;
  confidence: number | null;
  sources: { chunk_id: string }[];
}

export interface ResourceTagGroupUpdate {
  group: ResourceTagAssignment['group'];
  tagIds: string[];
}

export async function getDocumentTags(documentId: string): Promise<ResourceTagAssignment[]> {
  const response = await apiGet<{ tags: ResourceTagAssignment[] }>(`resources/${documentId}/tags`, {
    meta: { globalError: false, context: 'get-document-tags' }
  });
  return response.data.tags;
}

export async function replaceDocumentTags(
  documentId: string,
  groups: ResourceTagGroupUpdate[]
): Promise<ResourceTagAssignment[]> {
  const response = await apiPut<{ tags: ResourceTagAssignment[] }>(
    `resources/${documentId}/tags`,
    {
      groups: groups.map((group) => ({
        group: group.group,
        tag_ids: group.tagIds
      }))
    },
    { meta: { globalError: false, context: 'replace-document-tags' } }
  );
  return response.data.tags;
}

export async function reviewDocumentTag(
  documentId: string,
  assignmentId: string,
  status: ExtractedFieldStatus
): Promise<ResourceTagAssignment[]> {
  const response = await apiPost<{ tags: ResourceTagAssignment[] }>(
    `resources/${documentId}/tags/${assignmentId}/review`,
    { status },
    { meta: { globalError: false, context: 'review-document-tag' } }
  );
  return response.data.tags;
}

export async function deleteDocumentTag(documentId: string, assignmentId: string): Promise<void> {
  await apiDelete(`resources/${documentId}/tags/${assignmentId}`, {
    meta: { globalError: false, context: 'delete-document-tag' }
  });
}

export async function retryAnalysis(documentId: string): Promise<void> {
  await apiPost(`files/analysis/${documentId}/retry`, undefined, {
    meta: { globalError: false, context: 'retry-analysis' }
  });
}

export interface FieldBoundingBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  page_number: number;
  normalised: boolean;
}

export async function getFieldBoundingBoxes(documentId: string, sourceIds: string[]): Promise<FieldBoundingBox[]> {
  if (!sourceIds.length) return [];
  const params = new URLSearchParams();
  sourceIds.forEach((id) => params.append('source_ids', id));
  const response = await apiGet<FieldBoundingBox[]>(
    `files/analysis/${documentId}/bounding-boxes?${params.toString()}`,
    { meta: { globalError: false, context: 'field-bounding-boxes' } }
  );
  return response.data;
}
